<script setup lang="ts">
interface Props {
	rowsCount: number
	wallsCount: number
	shapesCount: number
	selectionCount: number
	toolbarNote: string
	selectionActionLabel: string
	canUndo: boolean
	editMode: 'seats' | 'walls'
	seatEditMode: 'rows' | 'seats'
	pendingWallStart: { x: number; y: number } | null
	showGrid: boolean
	snapToGrid: boolean
	hasSelection: boolean
	saving?: boolean
}

const props = defineProps<Props>()

const { $translate } = useNuxtApp()
const _ = $translate as (key: string) => string

const emit = defineEmits<{
	(e: 'open-guide'): void
	(e: 'undo'): void
	(e: 'set-edit-mode', mode: 'seats' | 'walls'): void
	(e: 'set-seat-edit-mode', mode: 'rows' | 'seats'): void
	(e: 'cancel-pending-wall'): void
	(e: 'toggle-grid'): void
	(e: 'toggle-snap-to-grid'): void
	(e: 'add-row'): void
	(e: 'add-label'): void
	(e: 'add-shape'): void
	(e: 'trigger-import'): void
	(e: 'export-seatmap'): void
	(e: 'toggle-disable-selection'): void
	(e: 'delete-selection'): void
	(e: 'save-seatmap'): void
	(e: 'close-editor'): void
}>()

const setRowMode = () => {
	if (props.editMode !== 'seats') {
		emit('set-edit-mode', 'seats')
	}
	emit('set-seat-edit-mode', 'rows')
}

const setSeatMode = () => {
	if (props.editMode !== 'seats') {
		emit('set-edit-mode', 'seats')
	}
	emit('set-seat-edit-mode', 'seats')
}

const setWallMode = () => {
	emit('set-edit-mode', 'walls')
}
</script>

<template>
	<header class="toolbar panel panel-default">
		<div class="panel-body toolbar-body">
			<div class="toolbar-info">
				<h4 class="toolbar-title">
					{{ _('Seat map editor') }}
					<small class="toolbar-hint">{{ _('{rows} rows, {screens} screens, {selected} selected').replace('{rows}', String(rowsCount)).replace('{screens}', String(shapesCount)).replace('{selected}', String(selectionCount)) }}</small>
				</h4>
				<p v-if="toolbarNote" class="toolbar-note toolbar-hint"><i>{{ toolbarNote }}</i></p>
			</div>
			<div class="toolbar-actions btn-toolbar" role="toolbar">
				<div class="btn-group" role="group">
					<button type="button" class="btn btn-default btn-sm" @click="emit('open-guide')"><i class="fa fa-fw fa-question-circle"></i> {{ _('Read guide') }}</button>
					<button type="button" class="btn btn-default btn-sm" :disabled="!canUndo" @click="emit('undo')"><i class="fa fa-fw fa-rotate-left"></i> {{ _('Undo') }}</button>
				</div>
				<div class="btn-group" role="group">
					<button
						type="button"
						class="btn btn-default btn-sm"
						:class="{ active: editMode === 'seats' && seatEditMode === 'rows' }"
						@click="setRowMode"
					>
						{{ _('Row mode') }}
					</button>
					<button
						type="button"
						class="btn btn-default btn-sm"
						:class="{ active: editMode === 'seats' && seatEditMode === 'seats' }"
						@click="setSeatMode"
					>
						{{ _('Seat mode') }}
					</button>
					<!-- Wall edit is temporarily hidden: the editor can draw/save walls, but the
					     production seat map viewer doesn't render them yet, so they'd be invisible
					     to customers. Kept in place since the underlying functionality still works
					     and is easy to re-enable once viewer support is added. -->
					<button v-if="false" type="button" class="btn btn-default btn-sm" :class="{ active: editMode === 'walls' }" @click="setWallMode">{{ _('Wall edit') }}</button>
				</div>
				<button v-if="editMode === 'walls'" type="button" class="btn btn-default btn-sm" :class="{ active: !!pendingWallStart }" @click="emit('cancel-pending-wall')">{{ _('Cancel draw') }}</button>
				<div class="btn-group" role="group">
					<button type="button" class="btn btn-default btn-sm" :class="{ active: showGrid }" @click="emit('toggle-grid')">{{ _('Grid') }}</button>
					<button v-if="showGrid" type="button" class="btn btn-default btn-sm" :class="{ active: snapToGrid }" @click="emit('toggle-snap-to-grid')">{{ _('Snap') }}</button>
				</div>
				<div class="btn-group" role="group">
					<button type="button" class="btn btn-default btn-sm" @click="emit('add-row')"><i class="fa fa-fw fa-plus"></i> {{ _('Add row') }}</button>
					<!-- Freestanding text labels are temporarily hidden for the same reason as
					     Wall edit above: the production viewer doesn't render them yet. -->
					<button v-if="false" type="button" class="btn btn-default btn-sm" @click="emit('add-label')"><i class="fa fa-fw fa-font"></i> {{ _('Add label') }}</button>
					<button v-if="editMode === 'seats'" type="button" class="btn btn-default btn-sm" @click="emit('add-shape')"><i class="fa fa-fw fa-tv"></i> {{ _('Add screen') }}</button>
				</div>
				<div class="btn-group" role="group">
					<button type="button" class="btn btn-default btn-sm" @click="emit('trigger-import')"><i class="fa fa-fw fa-upload"></i> {{ _('Import') }}</button>
					<button type="button" class="btn btn-default btn-sm" @click="emit('export-seatmap')"><i class="fa fa-fw fa-download"></i> {{ _('Export') }}</button>
				</div>
				<button v-if="editMode === 'seats'" type="button" class="btn btn-default btn-sm" :disabled="!hasSelection" @click="emit('toggle-disable-selection')">{{ selectionActionLabel }}</button>
				<button type="button" class="btn btn-danger btn-sm" :disabled="!hasSelection" @click="emit('delete-selection')"><i class="fa fa-fw fa-trash"></i> {{ _('Delete selected') }}</button>
				<div class="btn-group toolbar-endgroup" role="group">
					<button type="button" class="btn btn-default btn-sm" @click="emit('close-editor')"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
					<button type="button" class="btn btn-success btn-sm" :disabled="!!saving" @click="emit('save-seatmap')">
						<i :class="'fa fa-fw fa-' + (saving ? 'cog fa-spin' : 'save')"></i>
						{{ saving ? _('Saving...') : _('Save') }}
					</button>
				</div>
			</div>
		</div>
	</header>
</template>

<style scoped>
.toolbar {
	flex: 0 0 auto;
	margin-bottom: 0;
	border-radius: 0;
}

.toolbar-body {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 10px;
	padding: 10px 12px;
}

/* Fixed width so switching modes (which changes the hint text length)
   doesn't reflow/shift the action buttons on the right. */
.toolbar-info {
	flex: 0 0 320px;
	min-width: 0;
}

.toolbar-hint {
	color: #6c757d;
	font-size: 12px;
}

.toolbar-title {
	margin: 0;
	font-size: 14px;
	font-weight: bold;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.toolbar-title small {
	margin-left: 8px;
}

.toolbar-note {
	margin: 2px 0 0;
	font-size: 12px;
}

.toolbar-actions {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px;
	margin: 0;
	flex: 1 1 auto;
	justify-content: flex-end;
}

.toolbar-endgroup {
	margin-left: auto;
}

@media (max-width: 767px) {
	.toolbar-info {
		flex: 1 1 100%;
	}

	.toolbar-actions {
		flex: 1 1 100%;
		justify-content: flex-start;
	}
}

@media (orientation: landscape) and (max-height: 500px) {
	.toolbar-note {
		display: none;
	}
}
</style>